import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SEO from "../components/SEO";
import PageHeader from "../components/PageHeader";
import PhotoPlaceholder from "../components/PhotoPlaceholder";
import { Reveal } from "../components/Reveal";
import { IMAGES, isVideo } from "../lib/site";

const AXES = [
  {
    num: "01",
    title: "Haut Niveau",
    text: "Structurer et faire progresser les équipes féminines et masculines en compétition.",
    image: IMAGES.hero,
    position: "center 60%",
    alt: "Les coureurs du Club Cycliste Le Boulou",
    to: "/equipes",
    testId: "axe-competition",
  },
  {
    num: "02",
    title: "Formation",
    text: "Former et accompagner les jeunes vers la compétition.",
    image: IMAGES.ecole,
    position: "center 45%",
    alt: "Les jeunes de l'école de vélo du Club Cycliste Le Boulou",
    to: "/equipes/jeunes",
    testId: "axe-formation",
  },
  {
    num: "03",
    title: "Savoir Rouler, Sénégal & Loisir",
    text: "Développer le Savoir Rouler à Vélo dans les écoles, nos échanges avec le Sénégal et développer notre activité loisir.",
    image: null,
    to: "/projets/senegal",
    testId: "axe-savoir-rouler",
  },
  {
    num: "04",
    title: "Le club dans son territoire",
    text: "Faire du club un acteur majeur du cyclisme dans les Pyrénées-Orientales.",
    image: IMAGES.masculine,
    position: "center 20%",
    alt: "Un coureur du Club Cycliste Le Boulou en compétition",
    to: "/projets/projet-sportif",
    testId: "axe-territoire",
  },
];

export default function Projets() {
  return (
    <>
      <SEO
        title="Nos Projets"
        description="Le projet sportif du Club Cycliste Le Boulou : compétition, formation, Savoir Rouler à Vélo, échanges avec le Sénégal et loisir."
      />
      <PageHeader kicker="Nos Projets" title="Un club, 4 axes" />

      <section className="pt-16 sm:pt-24">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <Reveal>
            <div data-testid="projet-sportif-featured" className="group relative overflow-hidden bg-navy">
              <video
                autoPlay
                muted
                loop
                playsInline
                className="absolute inset-0 w-full h-full object-cover opacity-40 transition-transform duration-700 group-hover:scale-105"
              >
                <source src={IMAGES.feminine} type="video/mp4" />
                <source src={IMAGES.feminine.replace(/\.mp4$/i, ".webm")} type="video/webm" />
              </video>
              <div className="relative px-7 py-16 sm:px-14 sm:py-24 max-w-3xl">
                <p className="font-heading text-xs tracking-[0.3em] text-rouge font-semibold uppercase">Notre projet sportif</p>
                <h2 className="mt-4 font-heading uppercase font-semibold tracking-tighter text-white text-3xl sm:text-5xl">
                  Une ambition, quatre axes
                </h2>
                <p className="mt-5 text-white/80 text-base sm:text-lg leading-relaxed">
                  Compétition, formation, Savoir Rouler à Vélo et ancrage territorial : découvrez en détail le projet
                  sportif qui porte le Club Cycliste Le Boulou, saison après saison.
                </p>
                <Link
                  to="/projets/projet-sportif"
                  data-testid="projet-sportif-featured-link"
                  className="mt-8 inline-flex items-center gap-3 bg-rouge text-white font-heading uppercase tracking-wider text-sm px-7 py-4 hover:bg-white hover:text-navy transition-[background-color,color] duration-300"
                >
                  Découvrir le projet sportif
                  <ArrowRight size={15} />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-24">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-5 items-stretch">
          {AXES.map((p, i) => (
            <Reveal key={p.num} delay={i * 0.08} className="h-full">
              <div
                data-testid={p.testId}
                className="group flex flex-col h-full bg-white border border-navy/10 hover:border-rouge transition-[border-color] duration-300"
              >
                <div className="relative overflow-hidden h-60 shrink-0">
                  {p.image ? (
                    isVideo(p.image) ? (
                      <video
                        autoPlay
                        muted
                        loop
                        playsInline
                        style={{ objectPosition: p.position || "center" }}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      >
                        <source src={p.image} type="video/mp4" />
                        <source src={p.image.replace(/\.mp4$/i, ".webm")} type="video/webm" />
                      </video>
                    ) : (
                      <img
                        src={p.image}
                        alt={p.alt}
                        loading="lazy"
                        style={{ objectPosition: p.position || "center" }}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    )
                  ) : (
                    <PhotoPlaceholder label="Photo du projet à venir" className="w-full h-full" />
                  )}
                  <span className="absolute top-4 left-4 bg-navy text-white font-heading text-xs tracking-[0.25em] px-3 py-1.5">
                    AXE {p.num}
                  </span>
                </div>
                <div className="flex flex-col flex-1 p-7">
                  <h2 className="font-heading uppercase font-semibold tracking-tight text-navy text-2xl sm:text-3xl">{p.title}</h2>
                  <p className="mt-3 text-sm text-steel leading-relaxed">{p.text}</p>
                  <Link
                    to={p.to}
                    data-testid={`${p.testId}-link`}
                    className="mt-7 inline-flex items-center justify-center gap-3 bg-rouge text-white font-heading uppercase tracking-wider text-sm px-6 py-3.5 hover:bg-navy transition-colors duration-300"
                  >
                    Découvrir
                    <ArrowRight size={15} />
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </>
  );
}
